import { useCallback, useRef, useState, useEffect } from "react";

// Synthesized cat sound effects via the Web Audio API. No external files.
export default function useAudio() {
  const ctxRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const ctx = useCallback(() => {
    if (typeof window === "undefined") return null;
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const tone = useCallback(
    (freq, dur, type = "sine", gain = 0.18, slideTo = null) => {
      if (mutedRef.current) return;
      const ac = ctx();
      if (!ac) return;
      const osc = ac.createOscillator();
      const g = ac.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ac.currentTime);
      if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, ac.currentTime + dur);
      g.gain.setValueAtTime(0.0001, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(gain, ac.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);
      osc.connect(g).connect(ac.destination);
      osc.start();
      osc.stop(ac.currentTime + dur + 0.05);
    },
    [ctx]
  );

  const noise = useCallback(
    (dur, gain = 0.22) => {
      if (mutedRef.current) return;
      const ac = ctx();
      if (!ac) return;
      const buf = ac.createBuffer(1, ac.sampleRate * dur, ac.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
      const src = ac.createBufferSource();
      src.buffer = buf;
      const filter = ac.createBiquadFilter();
      filter.type = "highpass";
      filter.frequency.value = 1200;
      const g = ac.createGain();
      g.gain.setValueAtTime(gain, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);
      src.connect(filter).connect(g).connect(ac.destination);
      src.start();
    },
    [ctx]
  );

  const sfx = {
    purr: useCallback(() => {
      if (mutedRef.current) return;
      const ac = ctx();
      if (!ac) return;
      const osc = ac.createOscillator();
      const lfo = ac.createOscillator();
      const lfoGain = ac.createGain();
      const g = ac.createGain();
      osc.type = "sawtooth";
      osc.frequency.value = 55;
      lfo.frequency.value = 26;
      lfoGain.gain.value = 18;
      lfo.connect(lfoGain).connect(osc.frequency);
      g.gain.setValueAtTime(0.0001, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.14, ac.currentTime + 0.1);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 1.1);
      osc.connect(g).connect(ac.destination);
      osc.start();
      lfo.start();
      osc.stop(ac.currentTime + 1.2);
      lfo.stop(ac.currentTime + 1.2);
    }, [ctx]),
    meow: useCallback(() => {
      tone(620, 0.18, "sawtooth", 0.14, 420);
      setTimeout(() => tone(480, 0.22, "sawtooth", 0.12, 300), 120);
    }, [tone]),
    hiss: useCallback(() => noise(0.5, 0.26), [noise]),
    pop: useCallback(() => tone(880, 0.12, "triangle", 0.2, 1400), [tone]),
    pounce: useCallback(() => {
      tone(300, 0.1, "square", 0.18, 90);
      setTimeout(() => noise(0.25, 0.18), 60);
    }, [tone, noise]),
    win: useCallback(() => {
      [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => tone(f, 0.25, "triangle", 0.16), i * 110));
    }, [tone]),
  };

  return { sfx, muted, setMuted };
}
